import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Play, Pause, RotateCcw, Shield, Target, Radio, AlertTriangle, Zap } from 'lucide-react';

// 20-threat swarm: 10 air (▲), 6 surface (■), 4 underwater (●)
// 5 of them are "hardened" — soft-kill always fails → operator must authorize hard-kill
// Distribution of hardened: 3 air, 1 surface, 1 underwater
const SWARM_TEMPLATES = [
  // ---- AIR (10) ----
  { name: 'FPV Kamikaze',        domain: 'air',     color: 0xef4444, speed: 0.10, threatLevel: 'critical', hardened: true  },
  { name: 'Loitering Munition',  domain: 'air',     color: 0xef4444, speed: 0.09, threatLevel: 'critical', hardened: true  },
  { name: 'Armed MALE UAV',      domain: 'air',     color: 0xdc2626, speed: 0.07, threatLevel: 'critical', hardened: true  },
  { name: 'Recon Quadcopter',    domain: 'air',     color: 0xf59e0b, speed: 0.07, threatLevel: 'medium',   hardened: false },
  { name: 'Surveillance F-Wing', domain: 'air',     color: 0xf59e0b, speed: 0.08, threatLevel: 'low',      hardened: false },
  { name: 'Swarm Element 1',     domain: 'air',     color: 0xff6b6b, speed: 0.11, threatLevel: 'high',     hardened: false },
  { name: 'Swarm Element 2',     domain: 'air',     color: 0xff6b6b, speed: 0.10, threatLevel: 'high',     hardened: false },
  { name: 'Swarm Element 3',     domain: 'air',     color: 0xff6b6b, speed: 0.10, threatLevel: 'high',     hardened: false },
  { name: 'Decoy Drone Alpha',   domain: 'air',     color: 0xfbbf24, speed: 0.08, threatLevel: 'low',      hardened: false },
  { name: 'Decoy Drone Bravo',   domain: 'air',     color: 0xfbbf24, speed: 0.08, threatLevel: 'low',      hardened: false },
  // ---- SURFACE (6) ----
  { name: 'Heavy USV Charlie',   domain: 'surface', color: 0x16a34a, speed: 0.05, threatLevel: 'critical', hardened: true  },
  { name: 'Surface USV Alpha',   domain: 'surface', color: 0x22c55e, speed: 0.05, threatLevel: 'high',     hardened: false },
  { name: 'Surface USV Bravo',   domain: 'surface', color: 0x22c55e, speed: 0.055, threatLevel: 'medium',  hardened: false },
  { name: 'Recon USV',           domain: 'surface', color: 0x16a34a, speed: 0.045, threatLevel: 'medium',  hardened: false },
  { name: 'Patrol USV',          domain: 'surface', color: 0x22c55e, speed: 0.048, threatLevel: 'medium',  hardened: false },
  { name: 'Strike USV',          domain: 'surface', color: 0x22c55e, speed: 0.052, threatLevel: 'high',    hardened: false },
  // ---- UNDERWATER (4) ----
  { name: 'Heavy Torpedo UUV',   domain: 'underwater', color: 0x1d4ed8, speed: 0.045, threatLevel: 'critical', hardened: true  },
  { name: 'Subsurface UUV-1',    domain: 'underwater', color: 0x3b82f6, speed: 0.035, threatLevel: 'medium',   hardened: false },
  { name: 'Subsurface UUV-2',    domain: 'underwater', color: 0x2563eb, speed: 0.04,  threatLevel: 'high',     hardened: false },
  { name: 'Subsurface UUV-3',    domain: 'underwater', color: 0x60a5fa, speed: 0.03,  threatLevel: 'medium',   hardened: false },
];

const SOFT_KILL_METHODS = {
  air: [
    { id: 'rf_jamming', label: 'RF Jamming', color: '#eab308', behavior: 'tumble_down' },
    { id: 'cyber_takeover', label: 'Cyber Takeover', color: '#a855f7', behavior: 'spiral_down' },
    { id: 'gps_spoofing', label: 'GPS Spoofing', color: '#3b82f6', behavior: 'wander_away' },
  ],
  surface: [
    { id: 'rf_jamming', label: 'RF Jamming', color: '#eab308', behavior: 'tumble_down' },
    { id: 'gps_spoofing', label: 'GPS Spoofing', color: '#3b82f6', behavior: 'wander_away' },
  ],
  underwater: [
    { id: 'acoustic_blaster', label: 'Acoustic Blaster', color: '#06b6d4', behavior: 'acoustic_pushback' },
  ],
};

const HARD_KILL_OPTIONS = [
  { id: 'directed_energy',    label: 'Directed Energy Weapon (DEW)', color: '#22c55e', effectColor: 0x00ff00, desc: 'High-energy laser, line-of-sight' },
  { id: 'anti_drone_gun',     label: 'Anti Drone Gun',                color: '#eab308', effectColor: 0xffcc00, desc: 'Rapid-fire kinetic projectile' },
  { id: 'kinetic_interceptor',label: 'Kinetic Interceptor',           color: '#f97316', effectColor: 0xff6600, desc: 'Hit-to-kill projectile' },
  { id: 'net_capture',        label: 'Net Capture',                   color: '#06b6d4', effectColor: 0x00ffff, desc: 'Forensic recovery, low collateral' },
  { id: 'anti_drone_missile', label: 'Anti-Drone Missile',            color: '#ef4444', effectColor: 0xff0000, desc: 'Guided missile intercept' },
];

// Heuristic-ranked recommendation per domain (best -> worst).
// Scenario 4 makes this deterministic so the operator always has a clear "AI pick".
const HARD_KILL_RANKING = {
  air: ['directed_energy', 'anti_drone_missile', 'anti_drone_gun', 'kinetic_interceptor', 'net_capture'],
  surface: ['anti_drone_gun', 'kinetic_interceptor', 'anti_drone_missile', 'directed_energy', 'net_capture'],
  underwater: ['anti_drone_missile', 'kinetic_interceptor', 'directed_energy', 'anti_drone_gun', 'net_capture'],
};

export default function SimulationScenario4() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationRef = useRef(null);

  const playingRef = useRef(false);
  const threatsRef = useRef([]);
  const spawnedRef = useRef(false);
  const queueRef = useRef([]); // queue of hard-kill prompts pending operator action
  const statsRef = useRef({ detected: 0, soft_killed: 0, hard_killed: 0, neutralized: 0 });
  const logsRef = useRef([]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [logs, setLogs] = useState([]);
  const [activeThreats, setActiveThreats] = useState([]);
  const [stats, setStats] = useState({ detected: 0, soft_killed: 0, hard_killed: 0, neutralized: 0 });
  const [hardKillPrompt, setHardKillPrompt] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const pushLog = useCallback((message, type = 'info') => {
    const entry = { ts: new Date().toLocaleTimeString(), message, type };
    logsRef.current = [...logsRef.current.slice(-60), entry];
    setLogs([...logsRef.current]);
  }, []);

  const bumpStat = useCallback((field) => {
    statsRef.current = { ...statsRef.current, [field]: statsRef.current[field] + 1 };
    setStats({ ...statsRef.current });
  }, []);

  const syncActiveThreats = useCallback(() => {
    const list = threatsRef.current
      .filter(t => t.visible && t.userData.status !== 'neutralized' && t.userData.status !== 'destroyed')
      .map(t => ({
        id: t.userData.id,
        name: t.userData.name,
        domain: t.userData.domain,
        status: t.userData.status,
        threatLevel: t.userData.threatLevel,
        hardened: t.userData.hardened,
        distance: t.position.distanceTo(new THREE.Vector3(0, 0, 0)).toFixed(1),
        soft_kill_label: t.userData.soft_kill_label,
        hard_kill_label: t.userData.hard_kill_label,
      }));
    setActiveThreats(list);
  }, []);

  const presentNextPrompt = useCallback(() => {
    setHardKillPrompt(curr => {
      if (curr) return curr;
      const next = queueRef.current.shift();
      setPendingCount(queueRef.current.length);
      return next || null;
    });
  }, []);

  const enqueuePrompt = useCallback((prompt) => {
    queueRef.current.push(prompt);
    setPendingCount(queueRef.current.length);
    presentNextPrompt();
  }, [presentNextPrompt]);

  const cbRef = useRef({ pushLog, bumpStat, syncActiveThreats, enqueuePrompt });
  useEffect(() => {
    cbRef.current = { pushLog, bumpStat, syncActiveThreats, enqueuePrompt };
  }, [pushLog, bumpStat, syncActiveThreats, enqueuePrompt]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    playingRef.current = false;
    threatsRef.current = [];
    spawnedRef.current = false;
    queueRef.current = [];

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020617);
    scene.fog = new THREE.FogExp2(0x020617, 0.0045);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(55, container.clientWidth / container.clientHeight, 0.1, 1000);
    camera.position.set(50, 38, 50);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 15;
    controls.maxDistance = 150;

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(10, 20, 10);
    scene.add(dirLight);

    // Ocean
    const ocean = new THREE.Mesh(
      new THREE.PlaneGeometry(360, 360),
      new THREE.MeshStandardMaterial({ color: 0x0c4a6e, transparent: true, opacity: 0.32, side: THREE.DoubleSide })
    );
    ocean.rotation.x = -Math.PI / 2;
    ocean.position.y = -1;
    scene.add(ocean);

    const grid = new THREE.GridHelper(260, 52, 0x1e3a5f, 0x0f2847);
    grid.position.y = -0.9;
    scene.add(grid);

    // Ship
    const shipGroup = new THREE.Group();
    const s = 0.6;
    const hull = new THREE.Mesh(new THREE.BoxGeometry(2*s, 1*s, 6*s), new THREE.MeshStandardMaterial({ color: 0xfbbf24, metalness: 0.5, roughness: 0.3 }));
    hull.position.set(0, -0.3*s, 0); shipGroup.add(hull);
    const deck = new THREE.Mesh(new THREE.BoxGeometry(1.8*s, 0.2*s, 5.5*s), new THREE.MeshStandardMaterial({ color: 0xf59e0b }));
    deck.position.set(0, 0.2*s, 0); shipGroup.add(deck);
    const bridge = new THREE.Mesh(new THREE.BoxGeometry(1.2*s, 1*s, 1.5*s), new THREE.MeshStandardMaterial({ color: 0xeab308 }));
    bridge.position.set(0, 0.8*s, -0.8*s); shipGroup.add(bridge);
    const dome = new THREE.Mesh(new THREE.SphereGeometry(0.22*s, 16, 16), new THREE.MeshStandardMaterial({ color: 0x0ea5e9, emissive: 0x0ea5e9, emissiveIntensity: 0.6 }));
    dome.position.set(0, 2.1*s, -0.8*s); shipGroup.add(dome);
    scene.add(shipGroup);

    // Defense zones (5km / 2km / 1km)
    const addZone = (radius, color, opacity) => {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(radius, 32, 32),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity, wireframe: true, side: THREE.DoubleSide })
      );
      sphere.userData.isZone = true;
      scene.add(sphere);
    };
    addZone(15, 0x0ea5e9, 0.05);
    addZone(6, 0xeab308, 0.08);
    addZone(3, 0xef4444, 0.10);
    [15, 6, 3].forEach(r => {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(r - 0.05, r + 0.05, 64),
        new THREE.MeshBasicMaterial({ color: 0x1e3a5f, side: THREE.DoubleSide })
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -0.8;
      scene.add(ring);
    });

    function createThreatMesh(domain, color) {
      let geo;
      if (domain === 'air') geo = new THREE.TetrahedronGeometry(0.85, 0);
      else if (domain === 'surface') geo = new THREE.BoxGeometry(1.1, 0.55, 0.85);
      else geo = new THREE.SphereGeometry(0.6, 24, 24);
      const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
        color, emissive: color, emissiveIntensity: 0.65, roughness: 0.4
      }));
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.85, 0.95, 32),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6, side: THREE.DoubleSide })
      );
      ring.rotation.x = Math.PI / 2;
      mesh.add(ring);
      return mesh;
    }

    function spawnSwarm() {
      if (spawnedRef.current) return;
      spawnedRef.current = true;
      const yMap = { air: () => 6 + Math.random() * 7, surface: () => 0.4, underwater: () => -3.5 - Math.random() * 2.5 };

      SWARM_TEMPLATES.forEach((tpl, idx) => {
        const angle = (idx / SWARM_TEMPLATES.length) * Math.PI * 2 + (Math.random() * 0.18);
        const dist = 24 + Math.random() * 7;
        const startPos = new THREE.Vector3(Math.cos(angle) * dist, yMap[tpl.domain](), Math.sin(angle) * dist);
        const id = `T-${String(idx + 1).padStart(2, '0')}`;
        const mesh = createThreatMesh(tpl.domain, tpl.color);
        mesh.position.copy(startPos);

        // Hardened threats get a red "danger halo" so the operator sees them coming
        if (tpl.hardened) {
          const halo = new THREE.Mesh(
            new THREE.RingGeometry(1.2, 1.45, 32),
            new THREE.MeshBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0.5, side: THREE.DoubleSide })
          );
          halo.rotation.x = Math.PI / 2;
          halo.userData.isHalo = true;
          mesh.add(halo);
        }

        mesh.userData = {
          id, name: tpl.name, domain: tpl.domain, color: tpl.color,
          speed: tpl.speed * (0.85 + Math.random() * 0.3),
          threatLevel: tpl.threatLevel, hardened: tpl.hardened,
          status: 'approaching', detected: false, tracking: false,
          softKillApplied: false, soft_kill_label: null, hard_kill_label: null,
        };
        scene.add(mesh);
        threatsRef.current.push(mesh);
      });

      cbRef.current.pushLog(`[ALERT] MASS SWARM INBOUND — ${SWARM_TEMPLATES.length} threats; ${SWARM_TEMPLATES.filter(t => t.hardened).length} hardened (hard-kill required)`, 'danger');
      cbRef.current.syncActiveThreats();
    }

    function updateThreats() {
      const ship = new THREE.Vector3(0, 0, 0);
      let stateChanged = false;

      threatsRef.current.forEach(threat => {
        if (!threat.visible) return;
        const ud = threat.userData;
        if (ud.status === 'neutralized' || ud.status === 'destroyed') return;

        // Soft-killed: branch on the kill behavior
        if (ud.status === 'soft_killed') {
          const beh = ud.kill_behavior;
          const colorHex = ud.kill_color || 0xffffff;
          ud.killTick = (ud.killTick || 0) + 1;

          if (beh === 'spiral_down') {
            const t = ud.killTick;
            const radius = 0.04 * t;
            const center = ud.killOrigin;
            threat.position.x = center.x + Math.cos(t * 0.18) * radius;
            threat.position.z = center.z + Math.sin(t * 0.18) * radius;
            threat.position.y -= 0.06;
            threat.rotation.y += 0.18;
            threat.rotation.x += 0.04;
            if (t % 4 === 0) {
              const p = new THREE.Mesh(
                new THREE.SphereGeometry(0.12, 8, 8),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.85 })
              );
              p.position.copy(threat.position);
              p.userData.isEffect = true;
              p.userData.life = 0.8;
              scene.add(p);
            }
          } else if (beh === 'tumble_down') {
            threat.position.y -= 0.07;
            threat.rotation.x += 0.13 + Math.sin(ud.killTick * 0.3) * 0.05;
            threat.rotation.z += 0.10 + Math.cos(ud.killTick * 0.4) * 0.05;
            threat.position.x += Math.sin(ud.killTick * 0.4) * 0.04;
            threat.position.z += Math.cos(ud.killTick * 0.35) * 0.04;
            if (ud.killTick % 3 === 0) {
              const spark = new THREE.Mesh(
                new THREE.SphereGeometry(0.14, 6, 6),
                new THREE.MeshBasicMaterial({ color: 0xfde047, transparent: true, opacity: 0.95 })
              );
              spark.position.copy(threat.position);
              spark.position.x += (Math.random() - 0.5) * 0.6;
              spark.position.y += (Math.random() - 0.5) * 0.6;
              spark.position.z += (Math.random() - 0.5) * 0.6;
              spark.userData.isEffect = true;
              spark.userData.life = 0.4;
              scene.add(spark);
            }
          } else if (beh === 'wander_away') {
            if (!ud.wanderDir) {
              const baseAway = threat.position.clone().normalize();
              const perp = new THREE.Vector3(-baseAway.z, 0, baseAway.x).multiplyScalar(0.5 * (Math.random() - 0.5));
              ud.wanderDir = baseAway.add(perp).normalize();
            }
            threat.position.add(ud.wanderDir.clone().multiplyScalar(0.09));
            if (ud.domain === 'air') threat.position.y += Math.sin(ud.killTick * 0.1) * 0.05;
            threat.rotation.y += 0.04;
            if (ud.killTick % 6 === 0) {
              const echo = new THREE.Mesh(
                new THREE.RingGeometry(0.5, 0.7, 24),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.6, side: THREE.DoubleSide })
              );
              echo.position.copy(threat.position);
              echo.lookAt(0, threat.position.y, 0);
              echo.userData.isEffect = true;
              echo.userData.life = 0.7;
              scene.add(echo);
            }
          } else if (beh === 'acoustic_pushback') {
            const radial = new THREE.Vector3(threat.position.x, 0, threat.position.z).normalize();
            threat.position.add(radial.multiplyScalar(0.10));
            threat.position.y -= 0.025;
            threat.rotation.x += 0.08;
            threat.rotation.z += 0.08;
            if (ud.killTick % 8 === 0) {
              const ringMid = new THREE.Vector3().lerpVectors(new THREE.Vector3(0, threat.position.y, 0), threat.position, 0.5);
              const ring = new THREE.Mesh(
                new THREE.RingGeometry(0.5, 0.7, 32),
                new THREE.MeshBasicMaterial({ color: 0x06b6d4, transparent: true, opacity: 0.75, side: THREE.DoubleSide })
              );
              ring.position.copy(ringMid);
              ring.rotation.x = -Math.PI / 2;
              ring.userData.isEffect = true;
              ring.userData.life = 1.0;
              ring.userData.expands = true;
              scene.add(ring);
            }
          }

          if (threat.material) {
            threat.material.transparent = true;
            threat.material.opacity = Math.max(0.0, (threat.material.opacity ?? 1) - 0.008);
          }
          const dist2 = threat.position.distanceTo(ship);
          const finished =
            (threat.material && threat.material.opacity <= 0.05) ||
            threat.position.y < -8 ||
            (beh === 'wander_away' && dist2 > 50) ||
            (beh === 'acoustic_pushback' && dist2 > 40);
          if (finished) {
            threat.visible = false;
            ud.status = 'neutralized';
            cbRef.current.bumpStat('neutralized');
            stateChanged = true;
          }
          return;
        }

        // Hard-kill destruction animation
        if (ud.status === 'hard_kill_active') {
          threat.scale.multiplyScalar(0.90);
          threat.rotation.x += 0.08;
          threat.rotation.z += 0.08;
          if (threat.scale.x < 0.08) {
            threat.visible = false;
            ud.status = 'destroyed';
            cbRef.current.bumpStat('neutralized');
            stateChanged = true;
          }
          return;
        }

        if (ud.status === 'awaiting_hard_kill') {
          const pulse = 1 + Math.sin(Date.now() * 0.012) * 0.3;
          threat.scale.setScalar(pulse);
          // hold position-ish; slight forward creep
          const dir = new THREE.Vector3().subVectors(ship, threat.position).normalize();
          threat.position.add(dir.multiplyScalar(ud.speed * 0.2));
          return;
        }

        // Approach
        const dist = threat.position.distanceTo(ship);
        const dir = new THREE.Vector3().subVectors(ship, threat.position).normalize();
        const targetY = ud.domain === 'air' ? 4 : ud.domain === 'underwater' ? -3 : 0.4;
        dir.y = (targetY - threat.position.y) * 0.02;
        threat.position.add(dir.multiplyScalar(ud.speed));
        threat.rotation.x += 0.012;
        threat.rotation.y += 0.018;
        const sc = 1 + Math.sin(Date.now() * 0.004) * 0.10;
        threat.scale.setScalar(sc);

        // Hardened halo pulse (independent visual)
        threat.children.forEach(ch => {
          if (ch.userData.isHalo) {
            ch.material.opacity = 0.4 + Math.abs(Math.sin(Date.now() * 0.006)) * 0.4;
          }
        });

        // 5km detect
        if (dist < 16 && !ud.detected) {
          ud.detected = true;
          ud.status = 'detected';
          const tag = ud.hardened ? '⚠ HARDENED' : ud.threatLevel.toUpperCase();
          cbRef.current.pushLog(`[5KM] ${ud.id} ${ud.name} DETECTED — ${tag} (${ud.domain})`, ud.hardened ? 'danger' : 'warning');
          cbRef.current.bumpStat('detected');
          stateChanged = true;
        }

        // 4km tracking
        if (dist < 13 && ud.detected && !ud.tracking) {
          ud.tracking = true;
          ud.status = 'tracking';
          cbRef.current.pushLog(`[TRACK] ${ud.id} sensors locked — ${(dist * 0.333).toFixed(1)}km`, 'info');
          stateChanged = true;
        }

        // 2km — soft-kill attempt
        if (dist < 7 && !ud.softKillApplied) {
          ud.softKillApplied = true;
          const methods = SOFT_KILL_METHODS[ud.domain];
          const sk = methods[Math.floor(Math.random() * methods.length)];
          ud.soft_kill_label = sk.label;
          ud.kill_behavior = sk.behavior;
          ud.kill_color = parseInt(sk.color.replace('#', '0x'));
          ud.killOrigin = threat.position.clone();

          cbRef.current.pushLog(`[2KM] ${ud.id} AUTO SOFT KILL: ${sk.label} deployed`, 'danger');
          const colorHex = ud.kill_color;

          // Engagement effect (no ship-to-drone beam — soft-kill only at this stage)
          if (sk.behavior === 'acoustic_pushback') {
            for (let i = 0; i < 4; i++) {
              const ring = new THREE.Mesh(
                new THREE.RingGeometry(0.4 + i * 0.5, 0.55 + i * 0.5, 48),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.9, side: THREE.DoubleSide })
              );
              ring.position.set(0, threat.position.y, 0);
              ring.rotation.x = -Math.PI / 2;
              ring.userData.isEffect = true;
              ring.userData.life = 1.0;
              ring.userData.expands = true;
              scene.add(ring);
            }
          } else if (sk.behavior === 'spiral_down') {
            const cage = new THREE.Mesh(
              new THREE.BoxGeometry(2, 2, 2),
              new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.5, wireframe: true })
            );
            cage.position.copy(threat.position);
            cage.userData.isEffect = true;
            cage.userData.life = 1.0;
            scene.add(cage);
          } else if (sk.behavior === 'wander_away') {
            const phantom = new THREE.Mesh(
              new THREE.SphereGeometry(0.4, 12, 12),
              new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.6 })
            );
            phantom.position.copy(threat.position);
            phantom.position.x += (Math.random() - 0.5) * 4;
            phantom.position.z += (Math.random() - 0.5) * 4;
            phantom.userData.isEffect = true;
            phantom.userData.life = 1.0;
            scene.add(phantom);
          } else {
            for (let i = 0; i < 4; i++) {
              const wave = new THREE.Mesh(
                new THREE.RingGeometry(0.4 + i * 0.6, 0.55 + i * 0.6, 32),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
              );
              wave.position.copy(threat.position);
              wave.lookAt(ship);
              wave.userData.isEffect = true;
              wave.userData.life = 1.0;
              scene.add(wave);
            }
          }

          setTimeout(() => {
            if (ud.hardened) {
              // Soft kill fails on hardened threats — escalate to operator
              ud.status = 'awaiting_hard_kill';
              cbRef.current.pushLog(`[ALERT] ${ud.id} SOFT KILL FAILED — ${ud.name} HARDENED — operator authorization required`, 'danger');
              cbRef.current.enqueuePrompt({
                drone_id: ud.id, name: ud.name, domain: ud.domain,
                threatLevel: ud.threatLevel, distance: (dist * 0.333).toFixed(1),
                soft_kill_attempted: sk.label,
                ranking: HARD_KILL_RANKING[ud.domain] || HARD_KILL_RANKING.air,
              });
            } else {
              ud.status = 'soft_killed';
              cbRef.current.pushLog(`[NEUTRALIZED] ${ud.id} soft kill SUCCESS — ${sk.label} effective`, 'success');
              cbRef.current.bumpStat('soft_killed');
            }
            cbRef.current.syncActiveThreats();
          }, 700 + Math.random() * 600);
          stateChanged = true;
        }
      });

      // Effects cleanup
      const toRemove = [];
      scene.children.forEach(c => {
        if (c.userData.isEffect) {
          c.userData.life -= 0.02;
          if (c.userData.expands) {
            c.scale.setScalar(1 + (1 - c.userData.life) * 4);
          }
          if (c.material) c.material.opacity = Math.max(c.userData.life, 0);
          if (c.userData.life <= 0) toRemove.push(c);
        }
      });
      toRemove.forEach(o => scene.remove(o));

      if (stateChanged) cbRef.current.syncActiveThreats();
    }

    let frame = 0;
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      frame++;
      if (playingRef.current) {
        if (!spawnedRef.current) spawnSwarm();
        updateThreats();
        if (frame % 15 === 0) cbRef.current.syncActiveThreats();
      }
      scene.children.forEach(c => { if (c.userData.isZone) c.rotation.y += 0.0008; });
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      playingRef.current = false;
      if (container && renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHardKill = (option_id) => {
    if (!hardKillPrompt) return;
    const option = HARD_KILL_OPTIONS.find(o => o.id === option_id);
    const target = threatsRef.current.find(t => t.userData.id === hardKillPrompt.drone_id);
    if (target && option && sceneRef.current) {
      pushLog(`[HARD KILL] ${target.userData.id} AUTHORIZED: ${option.label}`, 'danger');

      // Weapon-specific visual
      if (option_id === 'directed_energy') {
        const beam = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 1.5, 0), target.position.clone()]),
          new THREE.LineBasicMaterial({ color: option.effectColor })
        );
        beam.userData.isEffect = true;
        beam.userData.life = 1.0;
        sceneRef.current.add(beam);
      } else if (option_id === 'anti_drone_gun') {
        // Burst of tracer dots
        const dir = new THREE.Vector3().subVectors(target.position, new THREE.Vector3(0, 1, 0)).normalize();
        for (let i = 0; i < 8; i++) {
          const tracer = new THREE.Mesh(
            new THREE.SphereGeometry(0.18, 8, 8),
            new THREE.MeshBasicMaterial({ color: option.effectColor, transparent: true, opacity: 1 })
          );
          const t = (i + 1) / 8;
          tracer.position.copy(new THREE.Vector3(0, 1, 0).clone().lerp(target.position, t));
          tracer.position.y += (Math.random() - 0.5) * 0.3;
          tracer.userData.isEffect = true;
          tracer.userData.life = 0.8;
          sceneRef.current.add(tracer);
        }
        const dummy = dir; void dummy; // keep linter quiet about unused
      } else if (option_id === 'kinetic_interceptor') {
        // Single fast streak
        const trail = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 1.5, 0), target.position.clone()]),
          new THREE.LineBasicMaterial({ color: option.effectColor, linewidth: 2 })
        );
        trail.userData.isEffect = true;
        trail.userData.life = 0.7;
        sceneRef.current.add(trail);
      } else if (option_id === 'net_capture') {
        const net = new THREE.Mesh(
          new THREE.SphereGeometry(1.4, 12, 12),
          new THREE.MeshBasicMaterial({ color: option.effectColor, transparent: true, opacity: 0.4, wireframe: true })
        );
        net.position.copy(target.position);
        net.userData.isEffect = true;
        net.userData.life = 1.0;
        sceneRef.current.add(net);
      } else if (option_id === 'anti_drone_missile') {
        // Curved trail approximated as a line
        const trail = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 1.5, 0), target.position.clone()]),
          new THREE.LineBasicMaterial({ color: option.effectColor })
        );
        trail.userData.isEffect = true;
        trail.userData.life = 0.9;
        sceneRef.current.add(trail);
      }

      const explosion = new THREE.Mesh(
        new THREE.SphereGeometry(1.5, 16, 16),
        new THREE.MeshBasicMaterial({ color: option.effectColor, transparent: true, opacity: 0.9 })
      );
      explosion.position.copy(target.position);
      explosion.userData.isEffect = true;
      explosion.userData.expands = true;
      explosion.userData.life = 1.0;
      sceneRef.current.add(explosion);

      setTimeout(() => {
        target.userData.status = 'hard_kill_active';
        target.userData.hard_kill_label = option.label;
        bumpStat('hard_killed');
        pushLog(`[DESTROYED] ${target.userData.id} eliminated by ${option.label}`, 'success');
        syncActiveThreats();
      }, 250);
    }
    setHardKillPrompt(null);
    setTimeout(presentNextPrompt, 200);
  };

  const dismissHardKill = () => {
    if (!hardKillPrompt) return;
    const target = threatsRef.current.find(t => t.userData.id === hardKillPrompt.drone_id);
    if (target) {
      target.userData.status = 'tracking';
      target.userData.softKillApplied = false;
      pushLog(`[OPERATOR] ${hardKillPrompt.drone_id} hard-kill declined — threat resuming approach`, 'warning');
      syncActiveThreats();
    }
    setHardKillPrompt(null);
    setTimeout(presentNextPrompt, 200);
  };

  const togglePlay = () => {
    playingRef.current = !playingRef.current;
    setIsPlaying(playingRef.current);
    if (playingRef.current && logsRef.current.length === 0) {
      pushLog('Scenario 4 started — 20-threat saturation: 15 soft-kill + 5 hardened (operator authorization)', 'info');
    }
  };

  const resetSim = () => {
    playingRef.current = false;
    setIsPlaying(false);
    spawnedRef.current = false;
    queueRef.current = [];
    setPendingCount(0);
    setHardKillPrompt(null);
    logsRef.current = [];
    setLogs([]);
    setActiveThreats([]);
    statsRef.current = { detected: 0, soft_killed: 0, hard_killed: 0, neutralized: 0 };
    setStats({ ...statsRef.current });
    if (sceneRef.current) {
      const toRemove = [];
      sceneRef.current.children.forEach(c => {
        if (c.userData.isEffect) toRemove.push(c);
      });
      threatsRef.current.forEach(t => toRemove.push(t));
      toRemove.forEach(o => sceneRef.current.remove(o));
      threatsRef.current = [];
    }
    pushLog('Scenario 4 reset — Ready', 'info');
  };

  const getStatusColor = (s) => {
    const m = { approaching: 'text-zinc-400', detected: 'text-yellow-500', tracking: 'text-orange-400',
      awaiting_hard_kill: 'text-red-500 animate-pulse', soft_killed: 'text-green-400',
      hard_kill_active: 'text-red-500', destroyed: 'text-emerald-500', neutralized: 'text-emerald-500' };
    return m[s] || 'text-zinc-400';
  };
  const getLogColor = (t) => ({ warning: 'text-yellow-400', danger: 'text-red-400', success: 'text-emerald-400' }[t] || 'text-zinc-400');
  const domainBadge = (d) => ({
    air:        { c: 'text-sky-400 bg-sky-500/10 border-sky-500/40',         label: 'AIR ▲'  },
    surface:    { c: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40', label: 'SURF ■' },
    underwater: { c: 'text-blue-400 bg-blue-500/10 border-blue-500/40',     label: 'SUB ●'  },
  }[d] || { c: '', label: d });
  const threatLevelColor = (l) => ({
    critical: 'text-red-500', high: 'text-orange-500', medium: 'text-yellow-500', low: 'text-green-500'
  }[l] || 'text-zinc-400');

  const totalThreats = SWARM_TEMPLATES.length;
  const allDone = stats.neutralized >= totalThreats;

  return (
    <div className="h-full flex flex-col bg-zinc-950" data-testid="simulation-scenario4">
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative">
          <div ref={containerRef} className="w-full h-full" />

          <div className="absolute top-4 left-4 flex gap-2" data-testid="sim4-stats">
            {[
              { label: 'Inbound',     val: totalThreats,         color: 'text-zinc-300 border-zinc-700 bg-zinc-800/70' },
              { label: 'Detected',    val: stats.detected,       color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' },
              { label: 'Soft Kill',   val: stats.soft_killed,    color: 'text-green-400 border-green-500/30 bg-green-500/10' },
              { label: 'Hard Kill',   val: stats.hard_killed,    color: 'text-red-400 border-red-500/30 bg-red-500/10' },
              { label: 'Neutralized', val: stats.neutralized,    color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
            ].map(st => (
              <div key={st.label} className={`px-3 py-2 rounded border backdrop-blur-md text-center ${st.color}`}>
                <div className="text-lg font-bold font-mono">{st.val}</div>
                <div className="text-[10px] uppercase">{st.label}</div>
              </div>
            ))}
          </div>

          <div className="absolute top-4 right-4 max-w-xs bg-black/70 backdrop-blur-md border border-red-500/40 p-3 rounded-sm">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Target size={14} className="text-red-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-red-400">Scenario 4 — Mass Saturation</h3>
            </div>
            <p className="text-[11px] text-zinc-400 leading-snug">
              <span className="text-amber-300">20-threat</span> swarm — <span className="text-emerald-300">15 soft-kill</span> + <span className="text-red-300">5 hardened</span> requiring operator-authorized hard-kill.
            </p>
            <div className="mt-2 space-y-0.5 text-[10px] font-mono text-zinc-400">
              <div><span className="text-sky-400">▲ AIR (10)</span> — 3 hardened</div>
              <div><span className="text-emerald-400">■ SURF (6)</span> — 1 hardened</div>
              <div><span className="text-blue-400">● SUB (4)</span> — 1 hardened</div>
            </div>
          </div>

          <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md border border-zinc-800 p-3 rounded-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 text-sky-500 flex items-center gap-1">
              <Shield size={14} /> Defense Layers
            </h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-sky-500" /><span className="text-zinc-400">5km — Detect &amp; Track</span></div>
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-yellow-500" /><span className="text-zinc-400">2km — Auto Soft Kill</span></div>
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-zinc-400">1km — Hard Kill Zone</span></div>
            </div>
          </div>

          {pendingCount > 0 && !hardKillPrompt && (
            <div className="absolute bottom-4 right-4 bg-red-500/15 border border-red-500/50 backdrop-blur-md p-2 rounded-sm text-xs font-mono text-red-300" data-testid="pending-banner">
              {pendingCount} more authorization{pendingCount === 1 ? '' : 's'} pending
            </div>
          )}

          {allDone && (
            <div className="absolute inset-x-0 top-1/3 flex items-center justify-center pointer-events-none" data-testid="sim4-all-clear">
              <div className="bg-emerald-500/15 backdrop-blur-md border-2 border-emerald-400 rounded-lg px-8 py-4 text-center">
                <div className="text-2xl font-bold uppercase tracking-widest text-emerald-300">All Clear</div>
                <div className="text-xs text-emerald-200 font-mono mt-1">Mass swarm fully neutralized — {stats.soft_killed} soft / {stats.hard_killed} hard</div>
              </div>
            </div>
          )}

          {hardKillPrompt && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/65 backdrop-blur-sm z-30" data-testid="s4-hard-kill-modal">
              <div className="bg-zinc-900 border-2 border-red-500 rounded-lg p-6 max-w-2xl w-full mx-4">
                <div className="flex items-center gap-2 text-red-500 mb-4">
                  <AlertTriangle size={24} />
                  <h3 className="text-lg font-bold uppercase">Hard Kill Authorization Required</h3>
                  {pendingCount > 0 && (
                    <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded bg-red-500/20 border border-red-500/50">
                      {pendingCount + 1} of {pendingCount + 1 + (queueRef.current.length || 0)}
                    </span>
                  )}
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded p-3 mb-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div><span className="text-zinc-500">Target:</span> <span className="text-white font-mono">{hardKillPrompt.drone_id}</span></div>
                    <div><span className="text-zinc-500">Type:</span> <span className="text-white">{hardKillPrompt.name}</span></div>
                    <div><span className="text-zinc-500">Threat:</span> <span className={`font-bold uppercase ${threatLevelColor(hardKillPrompt.threatLevel)}`}>{hardKillPrompt.threatLevel}</span></div>
                    <div><span className="text-zinc-500">Range:</span> <span className="text-white font-mono">{hardKillPrompt.distance}km</span></div>
                  </div>
                  <p className="text-xs text-red-400 mt-2">
                    Soft kill ({hardKillPrompt.soft_kill_attempted}) failed — target HARDENED. AI-ranked hard-kill options:
                  </p>
                </div>

                <div className="space-y-2 mb-4">
                  {hardKillPrompt.ranking.map((optId, i) => {
                    const opt = HARD_KILL_OPTIONS.find(o => o.id === optId);
                    if (!opt) return null;
                    const isTop = i === 0;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => handleHardKill(opt.id)}
                        className={`w-full text-left p-3 rounded border transition-all ${
                          isTop
                            ? 'border-emerald-500 bg-emerald-500/5 hover:bg-emerald-500/15'
                            : 'border-zinc-700 bg-zinc-800/50 hover:border-red-500 hover:bg-red-500/10'
                        }`}
                        data-testid={`s4-hardkill-${opt.id}`}
                      >
                        <div className="flex items-center gap-2">
                          {isTop && (
                            <span className="text-[9px] font-bold font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                              AI Recommended
                            </span>
                          )}
                          <span className="text-sm font-bold" style={{ color: opt.color }}>{opt.label}</span>
                          <span className="text-[10px] font-mono text-zinc-500 ml-auto">#{i + 1}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={dismissHardKill}
                  className="w-full py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-xs font-mono uppercase text-zinc-400 transition-colors"
                  data-testid="s4-decline-hardkill"
                >
                  Decline — Hold Fire (threat resumes approach)
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="w-80 bg-zinc-900/50 border-l border-zinc-800 flex flex-col">
          <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
            <button onClick={togglePlay}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded font-mono text-xs uppercase tracking-wider transition-all ${
                isPlaying ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' : 'bg-green-500/20 text-green-400 border border-green-500/50'
              }`} data-testid="s4-play-btn">
              {isPlaying ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Play</>}
            </button>
            <button onClick={resetSim}
              className="flex items-center gap-1 px-3 py-2 rounded font-mono text-xs uppercase bg-zinc-700/50 text-zinc-300 border border-zinc-600 hover:bg-zinc-700 transition-all"
              data-testid="s4-reset-btn">
              <RotateCcw size={14} /> Reset
            </button>
          </div>

          <div className="p-3 border-b border-zinc-800">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Target size={14} className="text-red-500" /> Active Threats
              <span className="ml-auto text-zinc-500 font-mono">{activeThreats.length}/{totalThreats}</span>
            </h3>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {activeThreats.length === 0 ? (
                <p className="text-xs text-zinc-500">{isPlaying ? 'All threats neutralized' : 'Press PLAY to spawn swarm'}</p>
              ) : (
                activeThreats.map(t => {
                  const b = domainBadge(t.domain);
                  return (
                    <div key={t.id} className="flex items-center justify-between text-xs bg-zinc-800/50 rounded px-2 py-1.5" data-testid={`s4-threat-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-white font-mono">{t.id}</span>
                          <span className={`text-[9px] px-1 rounded border ${b.c}`}>{b.label}</span>
                          {t.hardened && (
                            <span className="text-[9px] px-1 rounded border bg-red-500/15 text-red-300 border-red-500/40">⚠</span>
                          )}
                        </div>
                        <div className="text-zinc-500 truncate">{t.name}</div>
                        {t.soft_kill_label && (
                          <div className="text-[10px] text-amber-400 flex items-center gap-1"><Zap size={9} /> {t.soft_kill_label}</div>
                        )}
                        {t.hard_kill_label && (
                          <div className="text-[10px] text-red-400 flex items-center gap-1">⚔ {t.hard_kill_label}</div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-zinc-500 font-mono text-[10px]">{(parseFloat(t.distance) * 0.333).toFixed(1)}km</div>
                        <div className={`font-mono uppercase text-[10px] ${getStatusColor(t.status)}`}>{t.status.replace(/_/g, ' ')}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex-1 p-3 flex flex-col min-h-0">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Radio size={14} /> Event Log
            </h3>
            <div className="flex-1 overflow-y-auto space-y-0.5 font-mono text-[11px] bg-black/30 rounded p-2" data-testid="s4-event-log">
              {logs.length === 0 ? (
                <p className="text-zinc-500">Press PLAY to begin...</p>
              ) : (
                logs.map((l, i) => (
                  <div key={i} className={getLogColor(l.type)}>
                    <span className="text-zinc-600">[{l.ts}]</span> {l.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
